import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home, 
  ShoppingBag, 
  Package, 
  Store, 
  MapPin, 
  BarChart3, 
  Settings,
  X,
  Layers,
  TrendingUp,
  Users,
  FileText,
  Zap,
  Star,
  Activity
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SidebarProps {
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();

  const menuItems = [
    { 
      path: '/', 
      name: 'لوحة التحكم', 
      icon: Home,
      exact: true,
      color: 'primary',
      description: 'نظرة عامة على النظام'
    },
    { 
      path: '/offers', 
      name: 'إدارة العروض', 
      icon: ShoppingBag,
      badge: '12',
      color: 'secondary',
      description: 'العروض والفلايرز'
    },
    { 
      path: '/products', 
      name: 'إدارة المنتجات', 
      icon: Package,
      badge: '1.2K',
      color: 'accent',
      description: 'المنتجات المستخرجة'
    },
    { 
      path: '/merchants', 
      name: 'الهايبرماركت', 
      icon: Store,
      color: 'purple',
      description: 'المتاجر والعلامات التجارية'
    },
    { 
      path: '/cities', 
      name: 'المدن', 
      icon: MapPin,
      color: 'indigo',
      description: 'المناطق الجغرافية'
    },
    { 
      path: '/analytics', 
      name: 'الإحصائيات', 
      icon: BarChart3,
      color: 'success',
      description: 'التقارير والتحليلات'
    },
    { 
      path: '/settings', 
      name: 'الإعدادات', 
      icon: Settings,
      color: 'warning',
      description: 'إعدادات النظام'
    },
    { 
      path: '/api-test', 
      name: 'تجربة الربط', 
      icon: FileText,
      color: 'error',
      description: 'اختبار الاتصال'
    },
  ];

  const getIsActive = (item: any) => {
    return item.exact 
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path) && item.path !== '/';
  };

  return (
    <motion.div 
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -100, opacity: 0 }}
      className="sidebar fixed lg:relative z-50 h-full lg:h-auto bg-white/80 backdrop-blur-md border-r border-white/50"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200/50 bg-gradient-to-r from-primary-50/30 to-secondary-50/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-glow group-hover:scale-110 transition-transform duration-300">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold gradient-text">معالج العروض</h2>
            <p className="text-xs text-gray-600 flex items-center gap-1">
              <Star className="w-3 h-3 text-warning-500" />
              الإصدار 2.0
            </p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="lg:hidden p-2 rounded-xl hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-3">القائمة الرئيسية</p>
          {menuItems.map((item, index) => {
            const isActive = getIsActive(item);
            
            return (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive: linkActive }) => {
                    const active = item.exact ? linkActive : isActive;
                    return `sidebar-item ${active ? 'active' : ''} group relative overflow-hidden`;
                  }}
                >
                  {({ isActive: linkActive }) => {
                    const active = item.exact ? linkActive : isActive;
                    
                    return (
                      <>
                        {/* Background gradient for active state */}
                        {active && (
                          <motion.div
                            layoutId="activeBackground"
                            className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        
                        {/* Content */}
                        <div className="relative z-10 flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                            active 
                              ? 'bg-white/20 text-white shadow-glow' 
                              : `bg-${item.color}-100 text-${item.color}-600 group-hover:bg-${item.color}-200`
                          }`}>
                            <item.icon className="w-5 h-5" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold transition-colors duration-200 ${
                                active ? 'text-white' : 'text-gray-900 group-hover:text-primary-600'
                              }`}>
                                {item.name}
                              </span>
                              
                              {item.badge && (
                                <span className={`text-xs px-2 py-1 rounded-full font-medium transition-all duration-200 ${
                                  active 
                                    ? 'bg-white/20 text-white' 
                                    : `bg-${item.color}-100 text-${item.color}-600`
                                }`}>
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            
                            <p className={`text-xs mt-1 transition-colors duration-200 ${
                              active ? 'text-white/80' : 'text-gray-500 group-hover:text-gray-600'
                            }`}>
                              {item.description}
                            </p>
                          </div>
                        </div>
                        
                        {/* Hover effect */}
                        {!active && (
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        )}
                      </>
                    );
                  }}
                </NavLink>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 p-4 bg-gradient-to-br from-primary-50/50 to-secondary-50/50 rounded-xl border border-white/50">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary-600" />
            إحصائيات سريعة
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">العروض اليوم</span>
              <span className="text-sm font-bold text-primary-600">12</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">منتجات جديدة</span>
              <span className="text-sm font-bold text-secondary-600">156</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">دقة النظام</span>
              <span className="text-sm font-bold text-success-600">94%</span>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="mt-4 p-4 bg-gradient-to-br from-success-50/50 to-primary-50/50 rounded-xl border border-white/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-primary-500 rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">النظام يعمل بشكل مثالي</p>
              <p className="text-xs text-gray-600">جميع الخدمات متاحة</p>
            </div>
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50">
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 معالج العروض العربية
          </p>
          <p className="text-xs text-gray-400 mt-1">
            مدعوم بالذكاء الاصطناعي
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;